/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon,
  DetailStack,
  HStack,
  ICON_BRIEFCASE_OUTLINE,
  ICON_EYE_OUTLINE,
  Popover,
  SimpleTable,
  Stack,
  Text,
  Tooltip,
  UserPreview,
} from '@doveit/bricks';
import {
  JobApplicationSales,
  SerpJobApplication,
} from '../../../providers/api/dtos';
import { JobApplicationType } from '../../../domain/types';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import DateViewer from '../../../components/date-viewer/DateViewer';
import TextPreview from '../../../components/text-preview/TextPreview';
import UpdateJobApplicationStatus from '../update-job-application-status/UpdateJobApplicationStatus';
import { useJobApplicationSerpData } from '../../hooks/use-job-application-serp-data/useJobApplicationSerpData';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import { ThemeIcons } from '../../../theme/icons';
import ViewApplicantPopoverPreview from '../view-applicant-popover-preview/ViewApplicantPopoverPreview';
import * as styles from '../view-serp-job-application-table-row/ViewSerpJobApplicationTableRow.style';

export interface ViewJobApplicationRowProps extends React.AriaAttributes {
  serpJobApplication: SerpJobApplication,
  onJobApplicationUpdate?: VoidFunction
}

const ViewJobApplicationRow: React.FC<ViewJobApplicationRowProps> = ({
  serpJobApplication,
  onJobApplicationUpdate,
  ...rest
}) => {
  const {
    jobApplication,
    applicant,
    jobPositionData,
    note,
    goToJobApplicationPage,
  } = useJobApplicationSerpData(serpJobApplication);

  const { isFranchising, provinces } = React.useMemo(() => {
    if (jobApplication.applicationType === JobApplicationType.SALES) {
      const salesJobApplication = jobApplication as JobApplicationSales;
      return {
        isFranchising: salesJobApplication.isFranchising,
        provinces: salesJobApplication.provinces,
      };
    }
    return {
      isFranchising: undefined,
      provinces: undefined,
    };
  }, [jobApplication]);

  return (
    <SimpleTable.Row {...rest}>

      <SimpleTable.Cell
        aria-label="Candidato"
        width="10rem"
      >
        <ViewApplicantPopoverPreview
          applicantId={jobApplication.applicantId}
          isFranchising={isFranchising}
          trigger={(
            <div css={styles.applicantWrapper}>
              <UserPreview
                avatar={{ placeholder: ICON_BRIEFCASE_OUTLINE, icon: !isFranchising ? ThemeIcons.DOVE : undefined }}
                size="S"
                name={applicant?.name}
              >
                {applicant?.phoneNumber || applicant?.email}
              </UserPreview>
            </div>
          )}
        />

      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Posizione"
        width="7rem"
      >
        <DetailStack label="Posizione">
          <Tooltip content={jobPositionData.data?.name ?? 'Non presente'}>
            <div css={setVerticalEllipsis(1)}>
              {jobPositionData.data?.name ?? NO_VALUE_SYMBOL}
            </div>
          </Tooltip>
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Provincia">
        {(!provinces || provinces.length === 0) && (
          <DetailStack label="Provincia">
            {NO_VALUE_SYMBOL}
          </DetailStack>
        )}
        {provinces && provinces.length === 1 && (
          <DetailStack label="Provincia">
            <Tooltip content={provinces[0]}>
              <div css={setVerticalEllipsis(1)}>
                {provinces[0]}
              </div>
            </Tooltip>
          </DetailStack>
        )}
        {provinces && provinces.length > 1 && (
          <Popover
            aria-label="Visualizza tutte le province"
            trigger={(
              <DetailStack label="Province">
                <div css={setVerticalEllipsis(1)}>
                  {`${provinces[0]},+${provinces.length - 1}`}
                </div>
              </DetailStack>
            )}
            hasArrow
          >
            <div css={styles.provincesPopoverWrapper}>
              <Stack gap={75}>
                {provinces.map((province) => (
                  <Text.Caption key={province}>{province}</Text.Caption>
                ))}
              </Stack>
            </div>
          </Popover>
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Stato candidatura">
        <UpdateJobApplicationStatus
          jobApplication={jobApplication}
          size="XS"
          onSuccess={onJobApplicationUpdate}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Data creazione">
        <DetailStack label="Aggiunta">
          <DateViewer
            stringDate={jobApplication.createdAt!}
            checkIfToday
          />
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell
        withDivider={!!note}
      >
        <HStack
          gap={200}
          wrap="nowrap"
        >
          {note && (
            <TextPreview
              aria-label="Note"
              text={note.text}
              author={note.author}
              date={note.date}
            />
          )}
        </HStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Azioni"
        align="right"
      >
        <HStack gap={75} wrap="nowrap">
          <ActionIcon
            aria-label="Visualizza candidatura"
            label="Visualizza"
            size="S"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToJobApplicationPage}
          />
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewJobApplicationRow;
